import {
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { rowFromChartClick } from '@/lib/chart-click';
import type { PlotSpan } from '@/lib/chart-scrub';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PartialEdges } from '@/lib/partial-buckets';
import { MetricsChartTooltip } from '@/components/ui/charts/chart-tooltip';
import {
  ChartPartialLegend,
  hasPartialEdge,
} from '@/components/ui/charts/chart-partial';
import {
  ChartLegendLines,
  ChartLegendRow,
  type ChartLegendLine,
} from '@/components/ui/charts/chart-legend-items';
import {
  ChartPlotAreaReporter,
  ChartScrubOverlay,
} from '@/components/ui/charts/chart-scrub-overlay';
import {
  CHART_MARGIN,
  chartGridProps,
  chartXAxisProps,
  chartYAxisProps,
} from '@/components/ui/charts/chart-theme';

export type MetricsAreaChartProps = {
  data: Record<string, unknown>[];
  syncId?: string;
  syncMethod?: 'index' | 'value';
  className?: string;
  yAxisWidth?: number;
  yAxisOrientation?: 'left' | 'right';
  yAxisTickFormatter?: ComponentProps<typeof YAxis>['tickFormatter'];
  yAxisDomain?: ComponentProps<typeof YAxis>['domain'];
  /** Keeps `yAxisDomain` as given even when the data goes below/above it. */
  yAxisAllowDataOverflow?: boolean;
  hideLegend?: boolean;
  tooltipContent?: ComponentProps<typeof MetricsChartTooltip>['content'];
  /**
   * Makes the whole column of a bucket clickable. Receives the clicked data row.
   */
  onBucketClick?: (row: Record<string, unknown>) => void;
  /** Index of the row under the finger (mobile) or mouse (desktop); null on release/leave. */
  onActiveIndexChange?: (index: number | null) => void;
  /** Hides the Y axis (Início on mobile). */
  hideYAxis?: boolean;
  /** Partial edges of `data`; adds the "Em andamento" legend item (CHART-04). */
  partialLegend?: PartialEdges;
  /** Legend items of extra lines (previous period, site ticket). */
  legendLines?: ChartLegendLine[];
  children: ReactNode;
};

/**
 * Area chart shell with sales-chart axis/grid defaults. Pass {@link MetricsChartAreaSeries} as children.
 *
 * Below 768px (AD-021) recharts never receives the pointer: a
 * {@link ChartScrubOverlay} picks the row under the finger, the tooltip
 * follows it through `defaultIndex` and renders in a fixed reading above the
 * chart (`portal`), and a tap without drag works as the bucket click.
 */
export function MetricsAreaChart({
  data,
  syncId,
  syncMethod,
  className,
  yAxisWidth = 50,
  yAxisOrientation = 'right',
  yAxisTickFormatter,
  yAxisDomain,
  yAxisAllowDataOverflow,
  hideLegend = true,
  tooltipContent,
  onBucketClick,
  onActiveIndexChange,
  hideYAxis = false,
  partialLegend,
  legendLines,
  children,
}: MetricsAreaChartProps) {
  const isMobile = useIsMobile();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [readout, setReadout] = useState<HTMLDivElement | null>(null);
  const [plotArea, setPlotArea] = useState<PlotSpan | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const chart = (
    <ResponsiveContainer
      key={isMobile ? 'mobile' : 'desktop'}
      className={cn(
        'min-h-[200px] w-full',
        !isMobile && onBucketClick && '[&_.recharts-wrapper]:cursor-pointer',
        className
      )}
    >
      <RechartsAreaChart
        data={data}
        onClick={
          !isMobile && onBucketClick
            ? (state) => {
                const row = rowFromChartClick(state, data);
                if (row) onBucketClick(row);
              }
            : undefined
        }
        onMouseMove={
          !isMobile && onActiveIndexChange
            ? (state) =>
                onActiveIndexChange(
                  rowFromChartClick(state, data)
                    ? Number(state.activeIndex)
                    : null
                )
            : undefined
        }
        onMouseLeave={
          !isMobile && onActiveIndexChange
            ? () => onActiveIndexChange(null)
            : undefined
        }
        margin={CHART_MARGIN}
        syncId={isMobile ? undefined : syncId}
        syncMethod={isMobile ? undefined : syncMethod}
      >
        <defs>
          {/* Define the hatch pattern */}
          <pattern
            id="hatch"
            patternUnits="userSpaceOnUse"
            width="10"
            height="10"
          >
            <path
              d="M-2,2 l4,-4 M0,10 l10,-10 M8,12 l4,-4"
              style={{ stroke: 'hsl(var(--chart-4))', strokeWidth: 1 }} // Slightly thicker line for better visibility
            />
          </pattern>
        </defs>
        <CartesianGrid {...chartGridProps} />
        <XAxis {...chartXAxisProps} />
        <YAxis
          {...chartYAxisProps}
          width={yAxisWidth}
          orientation={yAxisOrientation}
          tickFormatter={yAxisTickFormatter}
          domain={yAxisDomain}
          allowDataOverflow={yAxisAllowDataOverflow}
          hide={hideYAxis}
        />
        {isMobile ? (
          <MetricsChartTooltip
            content={tooltipContent}
            variant="area"
            portal={readout}
            defaultIndex={activeIndex ?? undefined}
          />
        ) : (
          <MetricsChartTooltip content={tooltipContent} variant="area" />
        )}
        {isMobile ? <ChartPlotAreaReporter onChange={setPlotArea} /> : null}
        {(partialLegend && hasPartialEdge(partialLegend)) ||
        legendLines?.length ? (
          <Legend
            verticalAlign="bottom"
            align="left"
            content={
              <ChartLegendRow>
                {legendLines ? <ChartLegendLines lines={legendLines} /> : null}
                {partialLegend ? (
                  <ChartPartialLegend edges={partialLegend} variant="dash" />
                ) : null}
              </ChartLegendRow>
            }
          />
        ) : hideLegend ? (
          <Legend
            wrapperStyle={{
              color: 'hsl(var(--foreground) / 0.7)',
              display: 'none',
            }}
          />
        ) : null}
        {children}
      </RechartsAreaChart>
    </ResponsiveContainer>
  );

  if (!isMobile) return chart;

  return (
    <div
      ref={wrapperRef}
      className="relative h-full w-full max-md:scroll-mt-[var(--chart-anchor-offset,0px)]"
    >
      <div
        ref={setReadout}
        data-chart-readout
        className="pointer-events-none absolute inset-x-0 bottom-full z-20"
      />
      {chart}
      <ChartScrubOverlay
        count={data.length}
        scale="point"
        plotArea={plotArea}
        onIndexChange={(index) => {
          setActiveIndex(index);
          onActiveIndexChange?.(index);
        }}
        onTap={
          onBucketClick
            ? (index) => {
                const row = data[index];
                if (!row) return;
                wrapperRef.current?.scrollIntoView({ block: 'start' });
                onBucketClick(row);
              }
            : undefined
        }
      />
    </div>
  );
}
